// src/components/Projects/AdvancedEditor.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Spinner, Button } from 'react-bootstrap';
import api from '../../api/api';
import { getCurrentUser } from '../../utils/auth';

// 🟢 Import our new Universal Editor
import TeachingMaterialEditor from './TeachingMaterialEditor'; 
import { showToast } from '../../utils/toast';

const AdvancedEditor = () => {
    const { projectId, unitId } = useParams();
    const navigate = useNavigate();
    
    const user = getCurrentUser();
    const slug = user?.tenant_slug;

    // --- STATE ---
    const [loading, setLoading] = useState(true);
    const [contentId, setContentId] = useState(null); // The DB ID of the GeneratedContentBlock
    const [documentTitle, setDocumentTitle] = useState(`Unit ${unitId} Content`);
    
    // The main Markdown body for TipTap
    const [initialMarkdown, setInitialMarkdown] = useState('');
    
    // The ARCS Metadata for the extra Orchestration tab
    const [orchestrationData, setOrchestrationData] = useState({
        introduction: '',
        summary: '',
        conclusion: ''
    });

    // --- FETCH DATA ---
    useEffect(() => {
        const fetchContent = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('access_token');
                // Use your existing endpoint
                const res = await api.get(`/tenant/${slug}/projects/${projectId}/unit-content/${unitId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                const data = res.data;
                setContentId(data.id);
                setInitialMarkdown(data.content_body || "");
                
                // Populate the ARCS Metadata tab
                setOrchestrationData({
                    introduction: data.introduction || "",
                    summary: data.summary || "",
                    conclusion: data.conclusion || "" // Keywords
                });

            } catch (err) {
                console.error("Failed to load unit content", err);
                showToast("Could not load content. Please try again.", 'error');
            } finally {
                setLoading(false);
            }
        };
        
        if (projectId && unitId && slug) fetchContent();
    }, [projectId, unitId, slug]);

    // --- SAVE DATA ---
    const handleSave = async (markdownContent) => {
        try {
            const token = localStorage.getItem('access_token');
            // Update your GeneratedContentBlocks table exactly as you did before!
            await api.put(`/tenant/${slug}/content/${contentId}`, {
                introduction: orchestrationData.introduction,
                content_body: markdownContent, // The text from TipTap
                summary: orchestrationData.summary,
                conclusion: orchestrationData.conclusion 
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log("Auto-save successful at", new Date().toLocaleTimeString());
        } catch (err) {
            console.error("Save failed", err);
        }
    };

    if (loading) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-light">
                <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} aria-hidden="true" />
                <h5 className="mt-3 text-muted">Loading Orchestration Engine...</h5>
            </div>
        );
    }

    return (
        <div className="bg-light vh-100 d-flex flex-column">
            {/* Minimal Header for Navigation */}
            <div className="bg-white border-bottom p-2 d-flex align-items-center px-4">
                <Button variant="link" onClick={() => navigate(-1)} className="text-decoration-none text-secondary fw-bold p-0 me-3">
                    ← Back to Project
                </Button>
            </div>

            {/* 🟢 Render the Universal Editor in Orchestration Mode */}
            <div className="flex-grow-1 overflow-hidden">
                <TeachingMaterialEditor 
                    mode="orchestration"
                    initialContent={initialMarkdown}
                    tenantSlug={slug}
                    projectId={projectId}
                    documentTitle={documentTitle}
                    setDocumentTitle={setDocumentTitle}
                    orchestrationData={orchestrationData}
                    setOrchestrationData={setOrchestrationData}
                    onSave={handleSave}
                />
            </div>
        </div>
    );
};

export default AdvancedEditor;